import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { takePendingJoin } from './lib/pendingJoin';
import { useSession } from './hooks/useSession';
import { useActiveGame } from './hooks/useActiveGame';
import { useFriends } from './hooks/useFriends';
import { useSettings } from './hooks/useSettings';
import { prefetchFaceCards } from './lib/prefetch';
import { ensurePushSubscription } from './lib/push';
import { useInvites } from './hooks/useInvites';
import { InviteToasts } from './components/InviteToasts';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import GamePage from './pages/GamePage';
import StatsPage from './pages/StatsPage';
import SettingsPage from './pages/SettingsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import JoinRedirectPage from './pages/JoinRedirectPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import DataDeletionStatusPage from './pages/DataDeletionStatusPage';
import FriendsPage from './pages/FriendsPage';
import PublicStatsPage from './pages/PublicStatsPage';

export default function App() {
  const { user, loading, refresh } = useSession();
  const refreshActive = useActiveGame(s => s.refresh);
  const clearActive = useActiveGame(s => s.clear);
  const friendsAttach = useFriends(s => s.attach);
  const friendsRefresh = useFriends(s => s.refresh);
  const friendsReset = useFriends(s => s.reset);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { prefetchFaceCards(); }, []);

  // Redeem an invite link stashed by /g/:code before login. Doing it here —
  // not in the guest form — also covers OAuth sign-ins, which round-trip
  // through the provider and land back on "/" with a fresh session.
  const nav = useNavigate();
  useEffect(() => {
    if (!user) return;
    const code = takePendingJoin();
    if (code) nav(`/g/${code}`, { replace: true });
  }, [user, nav]);

  // Toggle the high-contrast body class whenever the setting changes.
  const highContrast = useSettings(s => s.highContrast);
  useEffect(() => {
    document.body.classList.toggle('hc', highContrast);
  }, [highContrast]);
  useEffect(() => {
    if (user) refreshActive();
    else clearActive();
  }, [user, refreshActive, clearActive]);
  useEffect(() => {
    if (user && !user.isGuest) {
      friendsRefresh();
      return friendsAttach();
    } else {
      friendsReset();
    }
  }, [user, friendsRefresh, friendsAttach, friendsReset]);

  // Heal the "permission granted but no PushSubscription on the server"
  // state: PWA reinstalls, cleared service workers, expired subscriptions
  // pruned server-side, etc. all leave the browser thinking it's opted in
  // but with no row in the DB, so invites silently never fire. Re-registers
  // and re-POSTs idempotently on every login.
  useEffect(() => {
    if (!user || user.isGuest) return;
    ensurePushSubscription().catch(() => { /* best-effort */ });
  }, [user]);

  // In-app invite toasts — emitted alongside the OS push so users with the
  // app open get a richer, click-to-join chip.
  const invitesAttach = useInvites(s => s.attach);
  const invitesClear = useInvites(s => s.clear);
  useEffect(() => {
    if (!user) { invitesClear(); return; }
    return invitesAttach();
  }, [user, invitesAttach, invitesClear]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-ink-200 animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <>
      {user && <InviteToasts />}
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/data-deletion/:code" element={<DataDeletionStatusPage />} />
        <Route path="/g/:code" element={<JoinRedirectPage />} />
        <Route element={<RequireAuth user={user} />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/game/:gameId" element={<GamePage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/players/:id" element={<PublicStatsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function RequireAuth({ user }: { user: any }) {
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}
